import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Drawer, Group, Progress, Text, Textarea } from '@mantine/core';
import { IconCamera, IconInfoCircle, IconScan, IconSend } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PmsPasswordDialog } from '../pms/PmsWorkParts.js';
import { RecordFileList, WarningAlert } from './FormRecordParts.js';
import classes from './Forms.module.css';
import { type AmendValues, amendSchema } from './record-forms.js';
import { appendFiles, approvalsGiven, latestFiles, moveFile, removeFile } from './record-model.js';
import type { FormWorkActions, RecordUploadProgress } from './source.js';
import {
  formErrorText,
  rejectedFilesText,
  uploadProgressPercent,
  uploadProgressText,
} from './status.js';
import type { DraftFile, FormRecordDetail } from './types.js';

export interface AmendInput {
  reason: string;
  note: string | null;
  files: DraftFile[];
  /** Parola isteğe bağlı (ISM formu; ADR 0056). */
  password?: string;
}

/**
 * Değişiklik (Amend; ADR 0042 §13; mimari belge §7.4) [sorular #238]: gönderilmiş kayıtta her
 * değişiklik gerekçeli yeni revizyondur; önceki revizyon ve dosyaları görünür kalır. Değişiklik
 * yeni gönderimi aynı transaction'da üretir, akış ilk adımdan başlar. Parola yeniden istenir
 * (§12). Başlıktaki "Edit" belgenin revizyonudur; bu düğmenin adı bu yüzden "Amend"dir (§21).
 */
export function AmendDrawer({
  opened,
  record,
  work,
  onClose,
  onAmend,
}: {
  opened: boolean;
  record: FormRecordDetail;
  work?: FormWorkActions | undefined;
  onClose: () => void;
  onAmend: (input: AmendInput) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<DraftFile[]>(() => latestFiles(record));
  const [values, setValues] = useState<AmendValues | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [picking, setPicking] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  // Reddedilen dosyalar ve yükleme ilerlemesi (ADR 0055 §3, §4)
  const [rejected, setRejected] = useState<string | null>(null);
  const [uploading, setUploading] = useState<RecordUploadProgress | null>(null);
  const form = useForm<AmendValues>({
    resolver: zodResolver(amendSchema),
    defaultValues: { reason: '', note: '' },
  });
  useEffect(() => {
    if (opened) {
      form.reset({ reason: '', note: record.revisions.at(-1)?.note ?? '' });
      setFiles(latestFiles(record));
      setError(null);
      setWarnings([]);
      setValues(null);
    }
  }, [opened, record, form]);

  const pick = async (source: 'scan' | 'photo') => {
    if (!work) return;
    setPicking(true);
    setError(null);
    setRejected(null);
    const stop = work.onUploadProgress?.(setUploading);
    try {
      const picked = await work.pickFiles({
        title: t(source === 'scan' ? 'forms.files.addScan' : 'forms.files.addPhoto'),
        source,
      });
      setFiles((current) => appendFiles(current, picked.files));
      setWarnings(picked.files.flatMap((p) => p.file.warnings));
      setRejected(rejectedFilesText(t, picked.failed));
    } catch (caught) {
      setError(caught);
    } finally {
      stop?.();
      setUploading(null);
      setPicking(false);
    }
  };

  const fieldError = (name: keyof AmendValues) => {
    const message = form.formState.errors[name]?.message;
    return message ? t(`forms.errors.${message}`) : undefined;
  };

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        position="right"
        size={560}
        title={t('forms.amend.title', {
          record: record.recordNo,
          revision: (record.status.revisionNo ?? 0) + 1,
        })}
        closeButtonProps={{ 'aria-label': t('forms.dialog.back') }}
      >
        <form
          className={classes.drawerStack}
          noValidate
          onSubmit={form.handleSubmit((submitted) => {
            if (files.length === 0) {
              setError({ code: 'NO_FILES' });
              return;
            }
            setValues(submitted);
          })}
        >
          <Alert color="blue" icon={<IconInfoCircle size={16} />}>
            {t('forms.amend.intro')}
          </Alert>
          {approvalsGiven(record) > 0 ? (
            // Verilmiş ön onaylar düşer (sahip kararı 2026-10-17; ADR 0069)
            <WarningAlert role="note">
              {t('forms.amend.dropsApprovals', { count: approvalsGiven(record) })}
            </WarningAlert>
          ) : null}
          <Textarea
            label={t('forms.amend.reason')}
            description={t('forms.amend.reasonHint')}
            required
            autosize
            minRows={2}
            maxLength={2000}
            {...form.register('reason')}
            error={fieldError('reason')}
          />
          <Textarea
            label={t('forms.record.note')}
            autosize
            minRows={2}
            maxLength={4000}
            {...form.register('note')}
            error={fieldError('note')}
          />
          <div>
            <p className={classes.label}>{t('forms.files.title')}</p>
            <RecordFileList
              files={files}
              editable
              onMove={(index, step) => setFiles((current) => moveFile(current, index, step))}
              onRemove={(index) => setFiles((current) => removeFile(current, index))}
            />
          </div>
          {work ? (
            <Group gap="sm">
              <Button
                variant="default"
                leftSection={<IconScan size={16} />}
                loading={picking}
                onClick={() => void pick('scan')}
              >
                {t('forms.files.addScan')}
              </Button>
              <Button
                variant="default"
                leftSection={<IconCamera size={16} />}
                loading={picking}
                onClick={() => void pick('photo')}
              >
                {t('forms.files.addPhoto')}
              </Button>
            </Group>
          ) : null}
          {uploading ? (
            <div className={classes.uploadProgress}>
              <Text size="xs" className={classes.muted} role="status">
                {uploadProgressText(t, uploading)}
              </Text>
              <Progress
                value={uploadProgressPercent(uploading)}
                size="sm"
                aria-label={uploadProgressText(t, uploading)}
              />
            </div>
          ) : null}
          {rejected ? <WarningAlert>{rejected}</WarningAlert> : null}
          {warnings.includes('LOW_RESOLUTION') ? (
            <WarningAlert>{t('forms.errors.LOW_RESOLUTION')}</WarningAlert>
          ) : null}
          {error ? (
            <Alert color="red" role="alert">
              {formErrorText(t, error)}
            </Alert>
          ) : null}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {t('forms.dialog.back')}
            </Button>
            <Button type="submit" leftSection={<IconSend size={16} />}>
              {t('forms.amend.submit')}
            </Button>
          </Group>
        </form>
      </Drawer>
      <PmsPasswordDialog
        opened={values !== null}
        title={t('forms.amend.confirmTitle')}
        reauthKind="ism_form"
        body={t('forms.amend.confirmBody', { record: record.recordNo })}
        reason="none"
        confirmLabel={t('forms.amend.submit')}
        errorText={(caught) => formErrorText(t, caught)}
        onClose={() => setValues(null)}
        onConfirm={async ({ password }) => {
          if (!values) return null;
          await onAmend({
            reason: values.reason,
            note: values.note === '' ? null : values.note,
            files,
            ...(password ? { password } : {}),
          });
          setValues(null);
          return null;
        }}
      />
    </>
  );
}
